import { ScrollElement } from '@focaldata/cin-ui-components'
import { memo, useContext } from 'react'
import DragAndDrop from '../../../components/DragAndDrop-react-dnd/DragAndDrop'
import {
  DraftQuestionnaireEntry,
  EntryType
} from '../../../data/model/questionnaire'
import useGetDraftQuestionnaire from '../../../hooks/questionnaire/useGetDraftQuestionnaire'
import GenericEntryLayout from '../../../layouts/GenericEntryLayout'
import SectionEntryLayout from '../../../layouts/SectionEntryLayout'
import { AddQuestionMenu } from '../AddQuestionMenu'
import { EmptySection } from '../EmptySection'
import EntryPlaceholder from '../EntryPlaceholder'
import { GenericEntryContainer } from '../GenericEntry'
import QuestionnaireContext from '../Questionnaire.context'
import { getNextEntryPosition, getScrollId } from '../Questionnaire.utils'
import { PlaceholderHeightsById } from '../QuestionsList/QuestionsList.hooks'
import { SectionHiddenInWaveProvider } from '../WaveVisibility/useWaveVisibility'
import { isHiddenInWave } from '../WaveVisibility/waveVisibility'
import { useDragAndDrop } from '../useDragAndDrop.hooks'
import SectionCardHeaderContainer from './SectionCardHeader.container'
import SectionEntryFooter from './SectionEntryFooter'

const SectionEntryContainer = ({
  entry,
  placeholderHeightsById,
  onChangeVisible
}: {
  entry: DraftQuestionnaireEntry<EntryType.SectionEntryType>
  placeholderHeightsById: PlaceholderHeightsById
  onChangeVisible: (index: number, isVisible: boolean) => void
}) => {
  const { flattenedEntries } = useGetDraftQuestionnaire()
  const { entryItem: sectionEntryItem } = entry
  const { entries: sectionEntries } = sectionEntryItem
  // @todo Legacy eslint violation – fix this when editing
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const hasSectionEntries = sectionEntries?.length > 0
  const sectionTitle =
    sectionEntryItem.title || `Section ${entry.contextPosition + 1}`
  const moveCard = useDragAndDrop()
  const {
    questionnaireState: { entriesToRender }
  } = useContext(QuestionnaireContext)

  // return empty section or list of generic entries
  const getContent = (
    entries: DraftQuestionnaireEntry[]
  ): JSX.Element | null => {
    return (
      <>
        {entries.map((entry) => {
          const currentIndex = flattenedEntries.findIndex(
            (flattennedListEntry) => flattennedListEntry === entry
          )
          return (
            <ScrollElement
              elementId={getScrollId(entry.id)}
              key={entry.number}
              partialVisibility
              onChangeVisible={(isVisible) =>
                onChangeVisible(currentIndex, isVisible)
              }
            >
              {entriesToRender.includes(currentIndex) ? (
                <DragAndDrop
                  position={entry.position}
                  id={entry.id}
                  type="main"
                  moveCard={moveCard}
                  // @todo Legacy eslint violation – fix this when editing
                  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                  path={`${sectionEntryItem?.sectionId}/${entry.id}`}
                  accept={['basic']}
                >
                  <GenericEntryContainer entry={entry} />
                </DragAndDrop>
              ) : (
                <EntryPlaceholder
                  entry={entry}
                  height={placeholderHeightsById[entry.id]}
                />
              )}
            </ScrollElement>
          )
        })}
      </>
    )
  }

  return (
    <GenericEntryLayout
      entryCard={
        <SectionEntryLayout
          header={
            <SectionCardHeaderContainer
              entryNumber={entry.number}
              entryId={entry.id}
              contextPosition={entry.contextPosition}
              sectionEntryItem={sectionEntryItem}
            />
          }
          content={
            hasSectionEntries ? (
              <SectionHiddenInWaveProvider
                value={isHiddenInWave(sectionEntryItem)}
              >
                {getContent(sectionEntries)}
              </SectionHiddenInWaveProvider>
            ) : (
              <EmptySection
                sectionId={sectionEntryItem.sectionId}
                nextPosition={entry.position + 1}
              />
            )
          }
          footer={<SectionEntryFooter title={sectionTitle} />}
        />
      }
      bottomActions={
        <AddQuestionMenu
          showSectionMenu={!entry.sectionId}
          position={getNextEntryPosition(entry)}
          sectionId={entry.sectionId}
        />
      }
    />
  )
}

export default memo(SectionEntryContainer)
